import type { PortableUltraStatEntry } from './postflopBuilders';
import { DEFAULT_ULTRA_SLOT_LABELS } from './ultraStatCatalog';

export const ULTRA_PREFS_KEY = 'portableUltraPrefs';

export type UltraGridSlot = string | null;

export type UltraStyleSlice = {
  labelColor?: string;
  valueColor?: string;
  labelFontSize?: number;
  valueFontSize?: number;
  background?: string;
};

export type UltraStatOverride = UltraStyleSlice & {
  hidden?: boolean;
  customLabel?: string;
  customValue?: string;
};

export type UltraCustomStat = {
  id: string;
  label: string;
  value: string;
};

export type UltraDisplayEntry = {
  key: string;
  label: string;
  value: string | number;
  isCustom: boolean;
};

export type UltraHeadColors = {
  playerNameColor: string;
  valueColor: string;
  suffixColor: string;
  positiveColor: string;
  negativeColor: string;
};

export type UltraPrefs = {
  columns: number;
  visibleStatCount: number;
  gridSlots: UltraGridSlot[];
  headColors: UltraHeadColors;
  labelColor: string;
  valueColor: string;
  labelFontSize: number;
  valueFontSize: number;
  rowMinHeight: number;
  itemBackground: string;
  columnOverrides: UltraStyleSlice[];
  statOverrides: Record<string, UltraStatOverride>;
  deletedLabels: string[];
  customStats: UltraCustomStat[];
};

export const DEFAULT_ULTRA_HEAD_COLORS: UltraHeadColors = {
  playerNameColor: '#f0f1e9',
  valueColor: '#dcfa54',
  suffixColor: '#b0bd58',
  positiveColor: '#b8f068',
  negativeColor: '#ffb86b',
};

export const DEFAULT_ULTRA_PREFS: UltraPrefs = {
  columns: 3,
  visibleStatCount: 6,
  gridSlots: DEFAULT_ULTRA_SLOT_LABELS.slice(0, 6).map((label) => label),
  headColors: { ...DEFAULT_ULTRA_HEAD_COLORS },
  labelColor: '#a8a99f',
  valueColor: '#f0f1e9',
  labelFontSize: 8,
  valueFontSize: 10,
  rowMinHeight: 18,
  itemBackground: 'transparent',
  columnOverrides: [],
  statOverrides: {},
  deletedLabels: [],
  customStats: [],
};

function clampColumns(value: number) {
  return Math.min(5, Math.max(1, Math.round(value)));
}

function clampVisibleStatCount(value: number) {
  return Math.max(1, Math.round(value));
}

function normalizeGridSlots(raw: Partial<UltraPrefs>, visibleStatCount: number): UltraGridSlot[] {
  const fallbackLabels = [...DEFAULT_ULTRA_SLOT_LABELS];

  let slots: UltraGridSlot[];
  if (Array.isArray(raw.gridSlots)) {
    slots = raw.gridSlots.map((slot) =>
      typeof slot === 'string' && slot.trim() ? slot.trim() : null,
    );
  } else {
    slots = Array.from({ length: visibleStatCount }, (_, index) => fallbackLabels[index] ?? null);
  }

  while (slots.length < visibleStatCount) {
    slots.push(null);
  }

  return slots.slice(0, visibleStatCount);
}

function normalizeHeadColors(raw: Partial<UltraHeadColors> | undefined): UltraHeadColors {
  return {
    playerNameColor: raw?.playerNameColor ?? DEFAULT_ULTRA_HEAD_COLORS.playerNameColor,
    valueColor: raw?.valueColor ?? DEFAULT_ULTRA_HEAD_COLORS.valueColor,
    suffixColor: raw?.suffixColor ?? DEFAULT_ULTRA_HEAD_COLORS.suffixColor,
    positiveColor: raw?.positiveColor ?? DEFAULT_ULTRA_HEAD_COLORS.positiveColor,
    negativeColor: raw?.negativeColor ?? DEFAULT_ULTRA_HEAD_COLORS.negativeColor,
  };
}

export function normalizeUltraPrefs(raw: Partial<UltraPrefs>): UltraPrefs {
  const visibleStatCount = clampVisibleStatCount(
    raw.visibleStatCount ??
      (raw as { visibleRows?: number }).visibleRows ??
      DEFAULT_ULTRA_PREFS.visibleStatCount,
  );

  return {
    columns: clampColumns(raw.columns ?? DEFAULT_ULTRA_PREFS.columns),
    visibleStatCount,
    gridSlots: normalizeGridSlots(raw, visibleStatCount),
    headColors: normalizeHeadColors(raw.headColors),
    labelColor: raw.labelColor ?? DEFAULT_ULTRA_PREFS.labelColor,
    valueColor: raw.valueColor ?? DEFAULT_ULTRA_PREFS.valueColor,
    labelFontSize: raw.labelFontSize ?? DEFAULT_ULTRA_PREFS.labelFontSize,
    valueFontSize: raw.valueFontSize ?? DEFAULT_ULTRA_PREFS.valueFontSize,
    rowMinHeight: raw.rowMinHeight ?? DEFAULT_ULTRA_PREFS.rowMinHeight,
    itemBackground: raw.itemBackground ?? DEFAULT_ULTRA_PREFS.itemBackground,
    columnOverrides: Array.isArray(raw.columnOverrides) ? raw.columnOverrides : [],
    statOverrides:
      raw.statOverrides && typeof raw.statOverrides === 'object' ? raw.statOverrides : {},
    deletedLabels: Array.isArray(raw.deletedLabels) ? raw.deletedLabels : [],
    customStats: Array.isArray(raw.customStats) ? raw.customStats : [],
  };
}

export function readUltraPrefs(): UltraPrefs {
  try {
    const stored = localStorage.getItem(ULTRA_PREFS_KEY);
    if (!stored) {
      return { ...DEFAULT_ULTRA_PREFS, columnOverrides: [], statOverrides: {}, customStats: [] };
    }
    return normalizeUltraPrefs(JSON.parse(stored) as Partial<UltraPrefs>);
  } catch {
    return { ...DEFAULT_ULTRA_PREFS, columnOverrides: [], statOverrides: {}, customStats: [] };
  }
}

export function writeUltraPrefs(prefs: UltraPrefs) {
  localStorage.setItem(ULTRA_PREFS_KEY, JSON.stringify(normalizeUltraPrefs(prefs)));
}

export function makeCustomStatId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function customStatKey(id: string) {
  return `custom:${id}`;
}

export function buildUltraDisplayEntries(
  statsEntries: PortableUltraStatEntry[],
  prefs: UltraPrefs,
): UltraDisplayEntry[] {
  const deleted = new Set(prefs.deletedLabels);
  const result: UltraDisplayEntry[] = [];

  for (const entry of statsEntries) {
    if (deleted.has(entry.label)) {
      continue;
    }

    const override = prefs.statOverrides[entry.label];
    if (override?.hidden) {
      continue;
    }

    result.push({
      key: entry.label,
      label: override?.customLabel?.trim() || entry.label,
      value: override?.customValue ?? entry.value,
      isCustom: false,
    });
  }

  for (const custom of prefs.customStats) {
    const key = customStatKey(custom.id);
    const override = prefs.statOverrides[key];
    if (override?.hidden) {
      continue;
    }

    result.push({
      key,
      label: override?.customLabel?.trim() || custom.label,
      value: override?.customValue ?? custom.value,
      isCustom: true,
    });
  }

  return result;
}

export function resolveUltraHeadStyle(prefs: UltraPrefs): Record<string, string> {
  const colors = normalizeHeadColors(prefs.headColors);

  return {
    '--ultra-head-player-color': colors.playerNameColor,
    '--ultra-head-value-color': colors.valueColor,
    '--ultra-head-suffix-color': colors.suffixColor,
    '--ultra-head-positive-color': colors.positiveColor,
    '--ultra-head-negative-color': colors.negativeColor,
  };
}

export function resolveUltraItemBackground(prefs: UltraPrefs, index: number, key: string) {
  const columnOverride = prefs.columnOverrides[index % prefs.columns] ?? {};
  const statOverride = prefs.statOverrides[key] ?? {};

  return statOverride.background ?? columnOverride.background ?? prefs.itemBackground;
}

export function resolveUltraItemStyle(
  prefs: UltraPrefs,
  index: number,
  key: string,
): Record<string, string | number> {
  const columnOverride = prefs.columnOverrides[index % prefs.columns] ?? {};
  const statOverride = prefs.statOverrides[key] ?? {};

  return {
    '--ultra-label-color': statOverride.labelColor ?? columnOverride.labelColor ?? prefs.labelColor,
    '--ultra-value-color': statOverride.valueColor ?? columnOverride.valueColor ?? prefs.valueColor,
    '--ultra-label-size': `${
      statOverride.labelFontSize ?? columnOverride.labelFontSize ?? prefs.labelFontSize
    }px`,
    '--ultra-value-size': `${
      statOverride.valueFontSize ?? columnOverride.valueFontSize ?? prefs.valueFontSize
    }px`,
    '--ultra-item-bg': resolveUltraItemBackground(prefs, index, key),
    minHeight: `${prefs.rowMinHeight}px`,
  };
}

export function deleteUltraEntry(prefs: UltraPrefs, entry: UltraDisplayEntry): UltraPrefs {
  if (entry.isCustom) {
    const id = entry.key.replace(/^custom:/, '');
    const statOverrides = { ...prefs.statOverrides };
    delete statOverrides[entry.key];

    return {
      ...prefs,
      customStats: prefs.customStats.filter((stat) => stat.id !== id),
      statOverrides,
    };
  }

  const deletedLabels = prefs.deletedLabels.includes(entry.label)
    ? prefs.deletedLabels
    : [...prefs.deletedLabels, entry.label];

  return { ...prefs, deletedLabels };
}

export function restoreUltraLabel(prefs: UltraPrefs, label: string): UltraPrefs {
  return {
    ...prefs,
    deletedLabels: prefs.deletedLabels.filter((item) => item !== label),
  };
}

export function setUltraGridSlot(
  prefs: UltraPrefs,
  index: number,
  label: string | null,
): UltraPrefs {
  const gridSlots = [...normalizeGridSlots(prefs, prefs.visibleStatCount)];
  if (index < 0 || index >= gridSlots.length) {
    return prefs;
  }

  gridSlots[index] = label?.trim() ? label.trim() : null;
  return normalizeUltraPrefs({ ...prefs, gridSlots });
}

export function addUltraGridRow(prefs: UltraPrefs): UltraPrefs {
  const columns = clampColumns(prefs.columns);
  const gridSlots = [...normalizeGridSlots(prefs, prefs.visibleStatCount)];

  for (let index = 0; index < columns; index += 1) {
    gridSlots.push(null);
  }

  return normalizeUltraPrefs({
    ...prefs,
    visibleStatCount: gridSlots.length,
    gridSlots,
  });
}
